// The pure half of `usePropertyNotes` (which notes of a contact's properties its card shows,
// and how they are labelled) — kept free of React and Firestore so it is testable on its own.
// See `usePropertyNotes.ts` for why the card reads them.
import { parseNoteTime, sortNotesNewestFirst } from "./notesData";
import type { RealEstateContact, UserNote } from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";

/** The cache key the notes tab's badge reads the property notes from (beside `notes:{id}`). */
export const propertyNotesCacheKey = (contactId: string): string => `property-notes:${contactId}`;

/**
 * At most this many linked listings are read. A seller is linked to one property, a few at
 * most; a contact linked to dozens (an investor typed as מוכר) is not worth a listener each.
 */
export const MAX_PROPERTY_NOTE_LISTINGS = 10;

export type LinkedListings = Pick<RealEstateContact, "sellingListingIds" | "rentingOutListingIds">;

/** The listings a contact is linked to as their seller / landlord — unique, in order, capped. */
export function linkedListingIdsOf(contact: LinkedListings | null | undefined): string[] {
  const ids = [...(contact?.sellingListingIds ?? []), ...(contact?.rentingOutListingIds ?? [])].filter(
    (id): id is string => typeof id === "string" && id.trim().length > 0
  );
  return Array.from(new Set(ids)).slice(0, MAX_PROPERTY_NOTE_LISTINGS);
}

type AddressLike = { street?: unknown; houseNumber?: unknown; city?: unknown } | null | undefined;

const text = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

/** "נשר 37, כרמיאל" — what a property note is labelled with. Undefined when nothing is known. */
export function propertyLabelOf(address: AddressLike): string | undefined {
  const street = [text(address?.street), text(address?.houseNumber)].filter(Boolean).join(" ");
  const label = [street, text(address?.city)].filter(Boolean).join(", ");
  return label || undefined;
}

/**
 * The property notes as the card shows them: each listing's own notes (mine) and teammates'
 * office-shared ones, tagged with the property, newest first. Pure — the hook below feeds it.
 */
export function buildPropertyNotes({
  listingIds,
  ownByListing,
  orgByListing,
  labels,
  userEmail,
  nameByEmail
}: {
  listingIds: readonly string[];
  ownByListing: Readonly<Record<string, readonly UserNote[]>>;
  orgByListing: Readonly<Record<string, readonly DisplayUserNote[]>>;
  labels: Readonly<Record<string, string | undefined>>;
  userEmail: string;
  nameByEmail: ReadonlyMap<string, string>;
}): DisplayUserNote[] {
  const collected: DisplayUserNote[] = [];
  for (const listingId of listingIds) {
    const listing = { listingId, label: labels[listingId] };
    for (const note of ownByListing[listingId] ?? []) {
      collected.push({ ...note, noteTime: parseNoteTime(note.noteTime), isMine: true, authorEmail: userEmail, listing });
    }
    for (const note of orgByListing[listingId] ?? []) {
      collected.push({ ...note, listing, authorName: nameByEmail.get(note.authorEmail) ?? note.authorEmail });
    }
  }
  return sortNotesNewestFirst(collected);
}
